import React, { useState, useEffect } from "react";
import { X, UserPlus } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const BulkAssignModal = ({ leadIds, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [selectedUser, setSelectedUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userTeams, setUserTeams] = useState([]);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoadingUsers(true);

      const teamsResponse = await apiService.getUserTeams();
      const teams = teamsResponse.teams || [];
      setUserTeams(teams);

      if (teams.length === 0) {
        setError("You are not a member of any team");
        setLoadingUsers(false);
        return;
      }

      const allMembers = new Map();

      for (const team of teams) {
        try {
          const membersResponse = await apiService.getTeamMembers(team.id);
          const members = membersResponse.members || [];

          members.forEach((member) => {
            if (member.user && member.status === "active") {
              allMembers.set(member.user.id, {
                id: member.user.id,
                name: member.user.name,
                email: member.user.email,
                profileImageUrl: member.user.profileImageUrl,
                role: member.user.role,
                teamRole: member.role,
                teamName: team.name,
              });
            }
          });
        } catch (err) {
          console.error(`Error fetching members for team ${team.id}:`, err);
        }
      }

      const uniqueMembers = Array.from(allMembers.values());
      setUsers(uniqueMembers);

      if (uniqueMembers.length === 0) {
        setError("No active team members found");
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setError("Failed to load team members");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUser) {
      setError("Please select a user");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiService.bulkAssignLeads(leadIds, selectedUser);
      if (response.success) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error assigning leads:", error);
      setError(error.response?.data?.message || "Failed to assign leads");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-md ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`p-6 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <UserPlus size={20} className="text-blue-400" />
            <h2
              className={`text-xl font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Assign Leads
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4">
            <p className="text-blue-400 text-sm">
              You are about to assign <strong>{leadIds.length}</strong> lead
              {leadIds.length !== 1 ? "s" : ""} to a team member.
            </p>
          </div>

          {userTeams.length > 0 && (
            <div
              className={`border rounded-lg p-3 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Showing members from your {userTeams.length} team
                {userTeams.length !== 1 ? "s" : ""}:
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                {userTeams.map((t) => t.name).join(", ")}
              </p>
            </div>
          )}

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Assign to *
            </label>
            {loadingUsers ? (
              <div
                className={`w-full px-3 py-2 border rounded-lg text-center text-sm ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-400"
                    : "bg-gray-50 border-gray-300 text-gray-500"
                }`}
              >
                Loading team members...
              </div>
            ) : (
              <select
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setError("");
                }}
                disabled={users.length === 0}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="">Select a team member...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} {user.email && `(${user.email})`}
                  </option>
                ))}
              </select>
            )}
            {users.length === 0 && !loadingUsers && (
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                No team members available. Please join a team first.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`p-6 border-t flex items-center gap-3 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
              isDarkMode
                ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || !selectedUser || loadingUsers}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Assigning..."
              : `Assign ${leadIds.length} Lead${leadIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;
